import { Injectable } from '@angular/core';
import { ListViewColumn } from './list-view.component';
import { isNil } from 'lodash-es';


export interface ISortingParams {
  orderBy: string;
  orderDirection: string;
}

export interface IPageParams {
  pageSize: number;
  pageIndex: number ;
}

export interface IPreferences {
  columns?: Array<string>;
  filters?: Object;
  pageParams?: Object;
  sortingParams?: Object;
}

@Injectable({
  providedIn: 'root',
})
export class ListViewPreferencesService<T> {

  PAGE_PARAMS = 'pageParams';
  SORTING_PARAMS = 'sortingParams';

  setColumnsToLocalStorage(title, columns) {
    const preferences = this.getUserPreferences();

    if (!preferences.hasOwnProperty(title)) {
      preferences[title] = {columns: {}};
    }
    preferences[title]['columns'] = columns
      // .filter( item => item.selected === true)
      .map(
        (item: ListViewColumn<T>) => {
          return { columnDef: item.columnDef, selected: item.selected};
        },
      );

    this.setUserPreferences(preferences);
  }

  setFiltersToLocalStorage(title, filters) {
    if (title === undefined) {
      return;
    }
    const preferences = this.getUserPreferences();

    if (!preferences.hasOwnProperty(title)) {
      preferences[title] = {filters: {}};
    }
    preferences[title]['filters'] = filters;

    this.setUserPreferences(preferences);
  }

  setUserAttrFiltersToLocalStorage(title, filters) {
    if (title === undefined) {
      return;
    }
    const preferences = this.getUserPreferences();

    if (!preferences.hasOwnProperty(title)) {
      preferences[title] = {userAttrFilters: {}};
    }
    preferences[title]['userAttrFilters'] = filters;

    this.setUserPreferences(preferences);
  }

  getSavedFilters(listPreferenceKey: string) {
    const preferences = this.getUserPreferences();

    if (preferences.hasOwnProperty(listPreferenceKey) && preferences[listPreferenceKey].hasOwnProperty('filters')) {
      return preferences[listPreferenceKey]['filters'];
    } else {
      return null;
    }
  }

  getSavedUserAttrFilters(listPreferenceKey: string) {
    const preferences = this.getUserPreferences();

    if (preferences.hasOwnProperty(listPreferenceKey) && preferences[listPreferenceKey].hasOwnProperty('userAttrFilters')) {
      return preferences[listPreferenceKey]['userAttrFilters'];
    } else {
      return null;
    }
  }

  appendUserAttrFilterToStorage(listPreferenceKey: string, filterKey: string, updatedFilterValues: object) {
    let newFilters;
    const savedFilters = this.getSavedUserAttrFilters(listPreferenceKey);

    if(savedFilters && savedFilters[filterKey]) {
      newFilters = {
        ...savedFilters,
        [filterKey]: {
          ...savedFilters[filterKey],
          ...updatedFilterValues
        }
      }
    } else {
      if (updatedFilterValues['operatorCode'] === undefined) {
        updatedFilterValues['operatorCode'] = 1;
        updatedFilterValues['symbol'] = '==';
      }
      newFilters = {
        ...savedFilters,
        [filterKey]: {
          ...updatedFilterValues
        }
      };
    }

    this.setUserAttrFiltersToLocalStorage(listPreferenceKey, newFilters);
  }

  deleteUserAttributeFilter(listPreferenceKey: string, filterKey: string) {
    const savedFilters = this.getSavedUserAttrFilters(listPreferenceKey);
    delete savedFilters[filterKey];
    this.setUserAttrFiltersToLocalStorage(listPreferenceKey, savedFilters);
  }

  setPageParametersToLocalStorage(title: string, params: IPageParams) {
    this.addChildPreference(title, this.PAGE_PARAMS , params);
  }

  setSortingParamsToLocalStorage(title: string, params: ISortingParams) {
    this.addChildPreference(title, this.SORTING_PARAMS , params);
  }

  setUserPreferences(preferences: any) {
    const newPreferences = this.getPreferences();
    const currentUser = JSON.parse(localStorage.getItem('currentUser'));
    const orgId = JSON.parse(localStorage.getItem('selectedOrganization'));

    const orgPreferences = newPreferences[orgId];
    if (isNil(orgPreferences)) {
      newPreferences[orgId] = {};
    }

    newPreferences[orgId][currentUser.userId] = preferences;

    localStorage.setItem('preferences', JSON.stringify(newPreferences));
  }

  getUserPreferences() {
    const preferences = this.getPreferences();
    const currentUser = JSON.parse(localStorage.getItem('currentUser'));
    const orgId = JSON.parse(localStorage.getItem('selectedOrganization'));

    const orgPreferences = preferences[orgId] || {};
    return orgPreferences[currentUser.userId] || {};
  }

  getListColumnPreferences(listIdentifier: string): { columnDef: string, selected: boolean }[] {
    this.initListPreferences(listIdentifier);
    let listPreferences = this.getUserPreferences()[listIdentifier];
    return isNil(listPreferences.columns) ? [] : listPreferences.columns;
  }

  loadSavedSortingParams(listPreferenceKey: string): any {
    return this.getChildPreference(listPreferenceKey, this.SORTING_PARAMS);
  }

  loadSavedPageParams(listPreferenceKey: string): any {
    return this.getChildPreference(listPreferenceKey, this.PAGE_PARAMS);
  }

  getPreferences(): any {
    const preferences = JSON.parse(localStorage.getItem('preferences') || '{}');

    if (Object.keys(preferences).length === 0) {
      localStorage.setItem('preferences', JSON.stringify({}));
    }

    return preferences;
  }

  private getChildPreference(preferenceTitle: string, childPreferenceKey: string): ISortingParams | IPageParams {
    const preferences = this.getUserPreferences();

    if (preferenceTitle === undefined) {
      return null;
    }

    if (preferences.hasOwnProperty(preferenceTitle) && preferences[preferenceTitle].hasOwnProperty(childPreferenceKey)) {
      return preferences[preferenceTitle][childPreferenceKey];
    }
    return null;
  }

  private addChildPreference(preferenceTitle: string, childPreferenceKey: string, childPreferenceObject: ISortingParams | IPageParams) {
    if (preferenceTitle === undefined) {
      return;
    }

    const preferences = this.getUserPreferences();

    if (!preferences.hasOwnProperty(preferenceTitle)) {
      preferences[preferenceTitle] = {};
      preferences[preferenceTitle][childPreferenceKey] = {};
    }
    preferences[preferenceTitle][childPreferenceKey] = childPreferenceObject;

    this.setUserPreferences(preferences);
  }

  private initListPreferences(listIdentifier: string) {
    const preferences = this.getUserPreferences();
    const listPreferences = preferences[listIdentifier];


    if (listIdentifier !== undefined &&
      (isNil(listPreferences) || isNil(listPreferences.columns))) {
      preferences[listIdentifier] = {
        columns: [],
        filters: {},
        pageParams: {},
        userAttrFilters: preferences[listIdentifier]?.userAttrFilters ? preferences[listIdentifier].userAttrFilters : {}
      };
      this.setUserPreferences(preferences);
    }
  }
}
